import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { IModel, SalesforceSink } from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';

export const salesforceSinkAdapts = {
    type: '*',
    writeBehavior: '*',
    writeBatchSize: '*',
    externalIdFieldName: '*',
    ignoreNullValues: '*',
    maxConcurrentConnections: '*',
    structure: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SalesforceSink),
    asynchronous: true,
    adapts: salesforceSinkAdapts
})
export class SalesforceSinkAdapter extends CopySinkAdapter<SalesforceSink, Model.SalesforceSink> {
    protected model(): IModel {
        return SalesforceSink.MODEL;
    }

    protected override async _toResource(model: SalesforceSink): Promise<Model.SalesforceSink> {
        const result: Model.SalesforceSink = {
            type: this.model().name
        };
        result.writeBatchSize = ValueAdapter.toResource(model, SalesforceSink.WRITE_BATCH_SIZE);
        result.writeBehavior = ValueAdapter.toResource(model, SalesforceSink.WRITE_BEHAVIOR);
        result.externalIdFieldName =
            model.writeBehavior &&
                typeof model.writeBehavior === 'string' &&
                model.writeBehavior.toLowerCase() === writeBehaviorOptionValues.insert
                ? undefined
                : ValueAdapter.toResource(model, SalesforceSink.EXTERNAL_ID_FIELD_NAME);
        result.ignoreNullValues = ValueAdapter.toResource(model, SalesforceSink.IGNORE_NULL_VALUES);
        CopySinkAdapter.maxConcurrentConnectionsToResource(model, result);
        CopySinkAdapter.toCommonCopySinkResource(model, result, this.context);
        return result;
    }

    protected override async _toModel(resource: Model.SalesforceSink, owner: Activity): Promise<SalesforceSink> {
        const result = Owned.create<SalesforceSink>(this.model(), owner);
        ValueAdapter.toModel(result, SalesforceSink.WRITE_BATCH_SIZE, resource.writeBatchSize);
        ValueAdapter.toModel(result, SalesforceSink.EXTERNAL_ID_FIELD_NAME, resource.externalIdFieldName);
        ValueAdapter.toModel(result, SalesforceSink.IGNORE_NULL_VALUES, resource.ignoreNullValues);

        // handle upper case and lower case
        let writeBehavior = resource.writeBehavior;
        if (writeBehavior && typeof writeBehavior === 'string') {
            const lowerCaseValue = writeBehavior.toLowerCase();
            switch (lowerCaseValue) {
                case writeBehaviorOptionValues.insert:
                    writeBehavior = writeBehaviorOptionValues.insert;
                    break;
                case writeBehaviorOptionValues.upsert:
                    writeBehavior = writeBehaviorOptionValues.upsert;
                    break;
                default:
                    break;
            }
        }
        ValueAdapter.toModel(result, SalesforceSink.WRITE_BEHAVIOR, writeBehavior);

        CopySinkAdapter.maxConcurrentConnectionsToModel(resource, result);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);
        return result;
    }
}
